import { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical, Pencil, X, Check } from "lucide-react";
import type { BoardResponse, TagResponse } from "@wizard-sounds/shared";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export function AdminBoards() {
  const [boards, setBoards] = useState<BoardResponse[]>([]);
  const [tags, setTags] = useState<TagResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [type, setType] = useState<"popular" | "tag">("tag");
  const [tagSlug, setTagSlug] = useState("");
  const [soundLimit, setSoundLimit] = useState(24);

  const fetchBoards = async () => {
    setLoading(true);
    try {
      const [boardData, tagData] = await Promise.all([
        api.admin.boards.list(),
        api.tags.list(),
      ]);
      setBoards(boardData);
      setTags(tagData);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  const resetForm = () => {
    setName("");
    setSlug("");
    setType("tag");
    setTagSlug("");
    setSoundLimit(24);
    setShowCreate(false);
    setEditingId(null);
  };

  const handleCreate = async () => {
    if (!name || !slug) return;
    await api.admin.boards.create({
      name,
      slug,
      type,
      tag_slug: type === "tag" ? tagSlug : null,
      sound_limit: soundLimit,
      sort_order: boards.length,
    });
    resetForm();
    fetchBoards();
  };

  const handleUpdate = async () => {
    if (!editingId || !name || !slug) return;
    await api.admin.boards.update(editingId, {
      name,
      slug,
      type,
      tag_slug: type === "tag" ? tagSlug : null,
      sound_limit: soundLimit,
    });
    resetForm();
    fetchBoards();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this board tab?")) return;
    await api.admin.boards.delete(id);
    fetchBoards();
  };

  const handleToggleActive = async (board: BoardResponse) => {
    await api.admin.boards.update(board.id, { is_active: board.is_active ? 0 : 1 });
    fetchBoards();
  };

  const startEdit = (board: BoardResponse) => {
    setEditingId(board.id);
    setName(board.name);
    setSlug(board.slug);
    setType(board.type);
    setTagSlug(board.tag_slug ?? "");
    setSoundLimit(board.sound_limit);
    setShowCreate(false);
  };

  const autoSlug = (val: string) => {
    setName(val);
    if (!editingId) {
      setSlug(
        val
          .toLowerCase()
          .replace(/[^\w\s-]/g, "")
          .replace(/[\s_]+/g, "-"),
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  const isEditing = editingId !== null;
  const showForm = showCreate || isEditing;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-display">Boards</h1>
        <button
          onClick={() => {
            resetForm();
            setShowCreate(true);
          }}
          className="flex items-center gap-1.5 rounded-xl bg-spark px-3 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft"
        >
          <Plus className="h-4 w-4" />
          Add board tab
        </button>
      </div>

      <p className="mt-1 text-sm text-fog">
        Manage tabs shown in the board/streamer view. Each tab shows up to its sound limit.
      </p>

      {showForm && (
        <div className="mt-4 rounded-[18px] border border-mist bg-slate p-4">
          <h3 className="mb-3 font-display text-sm font-semibold tracking-display">
            {isEditing ? "Edit board tab" : "New board tab"}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => autoSlug(e.target.value)}
                placeholder="e.g. Wizard's Choice"
                className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment outline-none focus:border-spark"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                Slug
              </label>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. wizards-choice"
                className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment outline-none focus:border-spark"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as "popular" | "tag")}
                className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment outline-none focus:border-spark"
              >
                <option value="popular">Popular (auto by play count)</option>
                <option value="tag">Tag (linked to a tag)</option>
              </select>
            </div>
            {type === "tag" && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                  Tag
                </label>
                <select
                  value={tagSlug}
                  onChange={(e) => setTagSlug(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment outline-none focus:border-spark"
                >
                  <option value="">Select a tag...</option>
                  {tags.map((t) => (
                    <option key={t.id} value={t.slug}>
                      {t.name} ({t.sound_count})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                Sound Limit
              </label>
              <input
                type="number"
                value={soundLimit}
                onChange={(e) => setSoundLimit(parseInt(e.target.value) || 24)}
                min={1}
                max={48}
                className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment outline-none focus:border-spark"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={isEditing ? handleUpdate : handleCreate}
              disabled={!name || !slug || (type === "tag" && !tagSlug)}
              className="flex items-center gap-1.5 rounded-xl bg-spark px-3 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              {isEditing ? "Save" : "Create"}
            </button>
            <button
              onClick={resetForm}
              className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm text-fog hover:bg-slate-raised hover:text-parchment"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {boards.length === 0 ? (
          <p className="py-10 text-center text-fog">No board tabs yet. Create one above.</p>
        ) : (
          boards.map((board) => (
            <div
              key={board.id}
              className={cn(
                "flex items-center gap-3 rounded-[18px] border border-mist bg-slate p-3 sm:p-4",
                !board.is_active && "opacity-50",
              )}
            >
              <GripVertical className="hidden h-4 w-4 shrink-0 text-fog sm:block" />

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-display text-sm font-semibold tracking-display text-parchment">
                    {board.name}
                  </span>
                  <span className="rounded-full bg-slate-raised px-2 py-0.5 font-mono text-[10px] text-fog">
                    {board.type === "popular" ? "auto:popular" : `tag:${board.tag_slug}`}
                  </span>
                  <span className="font-mono text-[10px] text-fog">
                    limit: {board.sound_limit}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleToggleActive(board)}
                  className={cn(
                    "rounded-lg px-2 py-1 font-mono text-[10px] transition-colors",
                    board.is_active
                      ? "bg-moss/15 text-moss"
                      : "bg-slate-raised text-fog hover:text-parchment",
                  )}
                >
                  {board.is_active ? "Active" : "Inactive"}
                </button>
                <button
                  onClick={() => startEdit(board)}
                  className="rounded-lg p-1.5 text-fog hover:bg-slate-raised hover:text-parchment"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(board.id)}
                  className="rounded-lg p-1.5 text-fog hover:bg-ember/10 hover:text-ember"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
